import { loadTheme } from 'office-ui-fabric-react';

export type ThemeMode = 'system' | 'light' | 'dark';

export const LSKEY_THEMEMODE = 'mm365-thememode';

const DARK_BODY_CLASS = 'mm-dark';

// Default Fluent UI light palette
const lightPalette = {
    themePrimary: '#0078d4',
    themeLighterAlt: '#eff6fc',
    themeLighter: '#deecf9',
    themeLight: '#c7e0f4',
    themeTertiary: '#71afe5',
    themeSecondary: '#2b88d8',
    themeDarkAlt: '#106ebe',
    themeDark: '#005a9e',
    themeDarker: '#004578',
    neutralLighterAlt: '#faf9f8',
    neutralLighter: '#f3f2f1',
    neutralLight: '#edebe9',
    neutralQuaternaryAlt: '#e1dfdd',
    neutralQuaternary: '#d0d0d0',
    neutralTertiaryAlt: '#c8c6c4',
    neutralTertiary: '#a19f9d',
    neutralSecondary: '#605e5c',
    neutralPrimaryAlt: '#3b3a39',
    neutralPrimary: '#323130',
    neutralDark: '#201f1e',
    black: '#000000',
    white: '#ffffff',
};

// Standard Fluent UI dark palette (theme designer "dark" preset)
const darkPalette = {
    themePrimary: '#2899f5',
    themeLighterAlt: '#02060a',
    themeLighter: '#061827',
    themeLight: '#0c2e49',
    themeTertiary: '#185b92',
    themeSecondary: '#2386d7',
    themeDarkAlt: '#3da2f6',
    themeDark: '#5caff7',
    themeDarker: '#84c2f9',
    neutralLighterAlt: '#262626',
    neutralLighter: '#2f2f2f',
    neutralLight: '#3d3d3d',
    neutralQuaternaryAlt: '#464646',
    neutralQuaternary: '#4d4d4d',
    neutralTertiaryAlt: '#6b6b6b',
    neutralTertiary: '#c8c8c8',
    neutralSecondary: '#d0d0d0',
    neutralPrimaryAlt: '#dadada',
    neutralPrimary: '#ffffff',
    neutralDark: '#f4f4f4',
    black: '#f8f8f8',
    white: '#1b1b1b',
};

export const getThemeMode = (): ThemeMode => {
    try {
        const value = localStorage.getItem(LSKEY_THEMEMODE);
        if (value === 'light' || value === 'dark' || value === 'system') {
            return value;
        }
    } catch { }
    return 'system';
};

const systemPrefersDark = (): boolean => {
    try {
        return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    } catch {
        return false;
    }
};

export const isDarkThemeActive = (): boolean => {
    const mode = getThemeMode();
    return mode === 'dark' || (mode === 'system' && systemPrefersDark());
};

export const applyTheme = () => {
    if (isDarkThemeActive()) {
        document.body.classList.add(DARK_BODY_CLASS);
        loadTheme({
            palette: darkPalette,
            semanticColors: {
                link: '#6cb8f6',
                linkHovered: '#8fc8f9',
            },
            isInverted: true,
        });
    } else {
        document.body.classList.remove(DARK_BODY_CLASS);
        loadTheme({ palette: lightPalette, isInverted: false });
    }
};

export const setThemeMode = (mode: ThemeMode) => {
    try {
        localStorage.setItem(LSKEY_THEMEMODE, mode);
    } catch { }
    applyTheme();
};

export const initTheme = () => {
    applyTheme();

    // Follow OS preference changes while in "system" mode
    try {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
            if (getThemeMode() === 'system') {
                applyTheme();
            }
        };
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', onChange);
        } else if (typeof (mq as any).addListener === 'function') {
            // Older webviews only support the deprecated API
            (mq as any).addListener(onChange);
        }
    } catch { }
};
